/**
 * The ONLY place in the client that calls fetch() against the Django API.
 *
 * - Same-origin session cookie auth (Nginx in prod, Vite proxy in dev).
 * - CSRF: the token is read from the `csrftoken` cookie on every unsafe request.
 *   Django rotates it at login, so it is never cached in JS.
 * - Every failure becomes an ApiError with a status the UI can branch on.
 */

const API_PREFIX = "/api";
const CSRF_COOKIE = "csrftoken";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiError extends Error {
  /** HTTP status, or 0 when the server couldn't be reached at all. */
  readonly status: number;
  /** Parsed response body, if any (DRF validation errors live here). */
  readonly data: unknown;

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

let csrfRequest: Promise<void> | null = null;

/** Make sure the csrftoken cookie exists. Safe to call repeatedly. */
export function ensureCsrfCookie(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) {
    return Promise.resolve();
  }
  if (!csrfRequest) {
    csrfRequest = fetch(`${API_PREFIX}/auth/csrf/`, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) {
          throw new ApiError(response.status, "Could not initialise a secure session.");
        }
      })
      .finally(() => {
        csrfRequest = null;
      });
  }
  return csrfRequest;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return null;
  }
  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return null;
}

function messageFor(status: number, data: unknown): string {
  if (data && typeof data === "object" && "detail" in data && typeof data.detail === "string") {
    return data.detail;
  }
  if (status >= 500) return "Coal City servers are having trouble. Try again shortly.";
  if (status === 401 || status === 403) return "You need to sign in to do that.";
  if (status === 404) return "Not found.";
  if (status === 429) return "Too many requests. Slow down a little.";
  return "Request failed.";
}

export type RequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  if (UNSAFE_METHODS.has(method)) {
    await ensureCsrfCookie();
    const token = readCookie(CSRF_COOKIE);
    if (token) {
      headers.set("X-CSRFToken", token);
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError(0, "Can't reach the Coal City servers. Check your connection.");
  }

  const data = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, messageFor(response.status, data), data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
};